import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LoadStatusBadge } from '../components/Badges';
import {
  Truck, MapPin, Calendar, DollarSign, Camera, FileText, 
  ClipboardCheck, ChevronRight, Package, AlertCircle, CheckCircle2,
  Navigation, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const DriverDashboard = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAssignedLoads();
  }, []);

  const loadAssignedLoads = async () => {
    try {
      const response = await api.get('/driver/assigned-loads');
      setLoads(response.data);
    } catch (error) {
      toast.error('Failed to load assigned loads');
    } finally {
      setLoading(false);
    }
  };

  const getNextAction = (load) => {
    if (!load.has_pickup_inspection) {
      return { label: 'Start Pickup Inspection', action: 'pickup', color: 'bg-blue-500' };
    }
    if (load.status === 'picked_up' && !load.has_delivery_inspection) {
      return { label: 'Complete Delivery Inspection', action: 'delivery', color: 'bg-green-500' };
    }
    if (load.has_delivery_inspection) {
      return { label: 'View Details', action: 'view', color: 'bg-gray-500' };
    }
    return { label: 'View Load', action: 'view', color: 'bg-gray-500' };
  };

  const handleAction = (load, action) => {
    if (action === 'pickup') {
      navigate(`/driver/inspection/${load.id}?type=pickup`);
    } else if (action === 'delivery') {
      navigate(`/driver/inspection/${load.id}?type=delivery`);
    } else {
      navigate(`/driver/load/${load.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#4ADE80] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16 pb-24 px-4">
      {/* Header */}
      <div className="max-w-lg mx-auto mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-[#4ADE80]/20 flex items-center justify-center">
            <Truck className="w-6 h-6 text-[#4ADE80]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Driver Dashboard</h1>
            <p className="text-sm text-gray-400">Welcome, {profile?.full_name || 'Driver'}</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="max-w-lg mx-auto mb-6">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#12151a] rounded-lg p-3 text-center border border-[#1a1d24]">
            <Package className="w-5 h-5 mx-auto text-blue-400 mb-1" />
            <div className="text-lg font-bold text-white">{loads.length}</div>
            <div className="text-xs text-gray-500">Active Loads</div>
          </div>
          <div className="bg-[#12151a] rounded-lg p-3 text-center border border-[#1a1d24]">
            <ClipboardCheck className="w-5 h-5 mx-auto text-green-400 mb-1" />
            <div className="text-lg font-bold text-white">
              {loads.filter(l => l.has_pickup_inspection).length}
            </div>
            <div className="text-xs text-gray-500">Picked Up</div>
          </div>
          <div className="bg-[#12151a] rounded-lg p-3 text-center border border-[#1a1d24]">
            <Camera className="w-5 h-5 mx-auto text-[#4ADE80] mb-1" />
            <div className="text-lg font-bold text-white">
              {loads.reduce((acc, l) => acc + (l.photo_count || 0), 0)}
            </div>
            <div className="text-xs text-gray-500">Photos</div>
          </div>
        </div>
      </div>

      {/* Loads List */}
      <div className="max-w-lg mx-auto space-y-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Package className="w-5 h-5 text-[#4ADE80]" />
          Your Assigned Loads
        </h2>

        {loads.length === 0 ? (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="py-12 text-center">
              <Truck className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No active loads assigned</p>
              <p className="text-sm text-gray-500 mt-1">Check the loadboard for available loads</p>
              <Button 
                onClick={() => navigate('/loadboard')}
                className="mt-4"
                variant="outline"
              >
                Browse Loadboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          loads.map((load) => {
            const nextAction = getNextAction(load);
            return (
              <Card 
                key={load.id} 
                className="bg-[#12151a] border-[#1a1d24] overflow-hidden"
              >
                <CardContent className="p-0">
                  {/* Status Bar */}
                  <div className="flex items-center justify-between px-4 py-2 bg-[#0a0c10] border-b border-[#1a1d24]">
                    <LoadStatusBadge status={load.status} />
                    <div className="flex items-center gap-2 text-xs">
                      {load.has_pickup_inspection && (
                        <span className="flex items-center gap-1 text-green-400">
                          <CheckCircle2 className="w-3 h-3" /> Pickup
                        </span>
                      )}
                      {load.has_delivery_inspection && (
                        <span className="flex items-center gap-1 text-green-400">
                          <CheckCircle2 className="w-3 h-3" /> Delivery
                        </span>
                      )}
                      {load.bol && (
                        <span className="flex items-center gap-1 text-[#4ADE80]">
                          <FileText className="w-3 h-3" /> BOL
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Load Details */}
                  <div className="p-4">
                    {/* Route */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <div className="w-0.5 h-8 bg-gray-700"></div>
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      </div>
                      <div className="flex-1 space-y-2">
                        <div>
                          <p className="text-sm font-medium text-white">{load.pickup_location}</p>
                          <p className="text-xs text-gray-500">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {new Date(load.pickup_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{load.delivery_location}</p>
                          {load.delivery_date && (
                            <p className="text-xs text-gray-500">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {new Date(load.delivery_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Vehicle & Price */}
                    <div className="flex items-center justify-between mb-4 text-sm">
                      <span className="text-gray-400">
                        {load.vehicle_make_model || 'Vehicle'} {load.vehicle_year && `(${load.vehicle_year})`}
                      </span>
                      <span className="text-[#4ADE80] font-bold">
                        ${load.price?.toLocaleString()}
                      </span>
                    </div>

                    {/* Action Button */}
                    <Button 
                      onClick={() => handleAction(load, nextAction.action)}
                      className={`w-full ${nextAction.color} hover:opacity-90`}
                      data-testid={`load-action-${load.id}`}
                    >
                      {nextAction.label}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>

                    {/* Quick Actions */}
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => navigate(`/driver/photos/${load.id}`)}
                      >
                        <Camera className="w-3 h-3 mr-1" />
                        Photos ({load.photo_count || 0})
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => navigate(`/driver/bol/${load.id}`)}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        BOL
                      </Button>
                      {(load.status === 'picked_up' || load.status === 'in_transit') && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 text-xs border-[#4ADE80] text-[#4ADE80]"
                          onClick={() => navigate(`/tracking/${load.id}`)}
                        >
                          <Navigation className="w-3 h-3 mr-1" />
                          Track
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Bottom Navigation for Mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#12151a] border-t border-[#1a1d24] px-4 py-3 flex justify-around md:hidden">
        <Link to="/driver" className="flex flex-col items-center text-[#4ADE80]">
          <Truck className="w-5 h-5" />
          <span className="text-xs mt-1">Loads</span>
        </Link>
        <Link to="/loadboard" className="flex flex-col items-center text-gray-500">
          <Package className="w-5 h-5" />
          <span className="text-xs mt-1">Find</span>
        </Link>
        <Link to="/notifications" className="flex flex-col items-center text-gray-500">
          <AlertCircle className="w-5 h-5" />
          <span className="text-xs mt-1">Alerts</span>
        </Link>
        <Link to="/profile" className="flex flex-col items-center text-gray-500">
          <Navigation className="w-5 h-5" />
          <span className="text-xs mt-1">Profile</span>
        </Link>
      </div>
    </div>
  );
};

export default DriverDashboard;
